interface AccordionItem {
  id: string;
  triggerEl: HTMLElement;
  targetEl: HTMLElement;
  iconEl?: HTMLElement;
  active?: boolean;
}

interface AccordionOptions {
  alwaysOpen: boolean;
  activeClasses: string;
  inactiveClasses: string;
  onOpen: (accordion: unknown, item?: AccordionItem) => void;
  onClose: (accordion: unknown, item?: AccordionItem) => void;
  onToggle: (accordion: unknown, item?: AccordionItem) => void;
}

export default defineNuxtPlugin(() => {
  const Default: AccordionOptions = {
    alwaysOpen: false,
    activeClasses: "bg-gray-50 dark:bg-slate-800 text-red-500",
    inactiveClasses: "text-slate-900 dark:text-white",
    onOpen: () => {},
    onClose: () => {},
    onToggle: () => {},
  };

  class Accordion {
    _items: AccordionItem[];
    _options: AccordionOptions;

    constructor(items: AccordionItem[] = [], options: Partial<AccordionOptions> = {}) {
      this._items = items;
      this._options = { ...Default, ...options };
      this._init();
    }

    _init() {
      this._items.forEach((item) => {
        if (item.active) this.open(item.id);

        item.triggerEl.addEventListener("click", () => {
          this.toggle(item.id);
        });
      });
    }

    getItem(id: string) {
      return this._items.find((item) => item.id === id);
    }

    open(id: string) {
      // Non-null assertion: callers pass ids sourced from `_items` themselves
      const item = this.getItem(id)!;

      if (!this._options.alwaysOpen) {
        this._items.forEach((i) => {
          if (i !== item) {
            i.triggerEl.classList.remove(
              ...this._options.activeClasses.split(" ")
            );
            i.triggerEl.classList.add(
              ...this._options.inactiveClasses.split(" ")
            );
            i.targetEl.classList.add("hidden");
            i.triggerEl.setAttribute("aria-expanded", "false");
            i.active = false;
            if (i.iconEl) i.iconEl.classList.remove("rotate-180");
          }
        });
      }

      item.triggerEl.classList.add(...this._options.activeClasses.split(" "));
      item.triggerEl.classList.remove(
        ...this._options.inactiveClasses.split(" ")
      );
      item.targetEl.classList.remove("hidden");
      item.triggerEl.setAttribute("aria-expanded", "true");
      item.active = true;
      if (item.iconEl) item.iconEl.classList.add("rotate-180");

      this._options.onOpen(this, item);
    }

    toggle(id: string) {
      const item = this.getItem(id)!;
      if (item.active) {
        this.close(id);
      } else {
        this.open(id);
      }
      this._options.onToggle(this, item);
    }

    close(id: string) {
      const item = this.getItem(id)!;
      item.triggerEl.classList.remove(
        ...this._options.activeClasses.split(" ")
      );
      item.triggerEl.classList.add(...this._options.inactiveClasses.split(" "));
      item.targetEl.classList.add("hidden");
      item.triggerEl.setAttribute("aria-expanded", "false");
      item.active = false;
      if (item.iconEl) item.iconEl.classList.remove("rotate-180");

      this._options.onClose(this, item);
    }
  }

  // Provide globally if needed
  return {
    provide: {
      Accordion,
    },
  };
});
